// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { isArray, debounce } from 'lodash';
import moment from 'moment';

import SearchTextField from '../../SearchTextField';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import useLog from '../../../../hooks/ModelInventory/useLogs';
import { isDateValid, isValueEmpty } from '../../../../utils/Utils';
import {
  DATE_FORMAT_HH_MM_SS,
  EMPTY_ARRAY,
} from '../../../../const/CommonConst';

const DRAWER_WIDTH = 480;

const SORT_ORDER = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
};

const ATTRIBUTE_LOG_TYPE = 'Attribute';
const PARAGRAPH_DATA_TYPE = 'Paragraph';

const buildBulletSx = (isFirst) => (theme) => ({
  position: 'relative',
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: isFirst
    ? theme?.palette?.historyTimeLine?.activeDot
    : theme?.palette?.historyTimeLine?.inactiveDot,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&::after': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme?.palette?.background?.paper,
    position: 'absolute',
  },
});

const connectorSx = (theme) => ({
  flex: 1,
  width: '2px',
  minHeight: 24,
  backgroundColor: theme?.palette?.historyTimeLine?.line,
});

//  Paragraph-shaped values look like `{ title, description }`.
const isParagraphValue = (value, extraData) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if (extraData?.attribute_data_type === PARAGRAPH_DATA_TYPE) {
    return true;
  }
  const keys = Object.keys(value);
  return (
    keys?.length > 0 &&
    keys.every((k) => k === 'title' || k === 'description') &&
    (typeof value.title === 'string' || typeof value.description === 'string')
  );
};

const formatPrimitive = (val) => {
  if (val === null || val === undefined || val === '') {
    return '-';
  }
  if (typeof val === 'string' && isDateValid(val)) {
    return moment(val).format(DATE_FORMAT_HH_MM_SS);
  }
  if (typeof val === 'boolean' || typeof val === 'number') {
    return String(val);
  }
  return val;
};

const valueToSearchText = (value, extraData) => {
  if (isValueEmpty(value)) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (isArray(value)) {
    return value?.map((v) => v?.value ?? v).join(', ');
  }
  if (isParagraphValue(value, extraData)) {
    return [value?.title, value?.description].filter(Boolean).join(' — ');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(', ');
  }
  return String(value);
};

/**
 * Renders paragraph-shaped `{ title, description }` objects as a header + body.
 */
const ParagraphValue = ({ value }) => {
  if (!value?.title && !value?.description) {
    return (
      <Typography variant="subtitle2" color="text.secondary">
        -
      </Typography>
    );
  }
  return (
    <Box>
      {value?.title ? (
        <Typography variant="subtitle2">{value.title}</Typography>
      ) : null}
      {value?.description ? (
        <Tooltip
          title={value.description || ''}
          arrow
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 400,
                maxHeight: 200,
                overflowY: 'auto',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'left',
                lineHeight: 1.5,
                display: 'block',
                p: 1.5,
              },
            },
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {value.description}
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
};

ParagraphValue.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

/**
 * Renders a generic object as one key/value.
 */
const ObjectValue = ({ value }) => {
  const theme = useTheme();
  const entries = Object.entries(value || {});
  if (!entries.length) {
    return (
      <Typography variant="subtitle2" color="text.secondary">
        -
      </Typography>
    );
  }
  return (
    <Box>
      {entries?.map(([key, val]) => {
        const displayVal =
          val && typeof val === 'object'
            ? JSON.stringify(val)
            : formatPrimitive(val);
        return (
          <Typography
            key={key}
            variant="subtitle2"
            noWrap
            title={displayVal}
            sx={{
              wordBreak: 'break-word',
              color: theme?.palette?.historyTimeLine?.objecetVal,
            }}
          >
            <Typography
              component="span"
              variant="subtitle2"
              sx={{ color: theme?.palette?.historyTimeLine?.objectKey }}
            >
              {key} :{' '}
            </Typography>
            {displayVal}
          </Typography>
        );
      })}
    </Box>
  );
};

ObjectValue.propTypes = {
  value: PropTypes.shape({}).isRequired,
};

const LogValue = ({ value, extraData }) => {
  if (isValueEmpty(value)) {
    return (
      <Typography variant="subtitle2" color="text.secondary">
        -
      </Typography>
    );
  }

  if (isArray(value)) {
    const text = value?.map((v) => v?.value ?? v).join(', ');
    return <Typography variant="subtitle2">{text || '-'}</Typography>;
  }

  if (typeof value === 'object') {
    if (isParagraphValue(value, extraData)) {
      return <ParagraphValue value={value} />;
    }
    return <ObjectValue value={value} />;
  }

  return (
    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
      {formatPrimitive(value)}
    </Typography>
  );
};

LogValue.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.shape({
          value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
          ]),
        }),
        PropTypes.shape({}),
      ])
    ),
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    PropTypes.shape({}),
  ]),
  extraData: PropTypes.shape({
    attribute_data_type: PropTypes.string,
    attribute_label: PropTypes.string,
    attribute_name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

const HistoryTimelineItem = ({ entry, isFirst, isLast }) => {
  const theme = useTheme();

  const bulletSx = useMemo(() => buildBulletSx(isFirst), [isFirst]);
  return (
    <Grid container spacing={1.5} sx={{ position: 'relative' }} wrap="nowrap">
      <Grid
        item
        sx={{
          width: 24,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0.5,
        }}
      >
        <Box sx={bulletSx} />
        {!isLast && <Box sx={connectorSx} />}
      </Grid>

      <Grid item xs sx={{ pb: 2.5, minWidth: 0 }}>
        <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
          <Grid item xs sx={{ minWidth: 0 }}>
            <LogValue
              value={entry?.current_value}
              extraData={entry?.extra_data}
            />
            <Typography
              variant="body2"
              color={theme?.palette?.historyTimeLine?.updatedText}
              sx={{ display: 'block', mt: 0.25 }}
            >
              Updated by {entry?.responsible_user || 'Unknown'}
            </Typography>
          </Grid>
          <Grid item sx={{ flexShrink: 0, textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              {entry?.modification_date
                ? moment(new Date(entry.modification_date)).format(
                    DATE_FORMAT_HH_MM_SS
                  )
                : '-'}
            </Typography>
            <Typography
              variant="body2"
              color={theme?.palette?.historyTimeLine?.updatedText}
              sx={{ display: 'block', mt: 0.25, whiteSpace: 'nowrap' }}
            >
              {entry?.state || '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

HistoryTimelineItem.propTypes = {
  entry: PropTypes.shape({
    state: PropTypes.string,
    responsible_user: PropTypes.string,
    modification_date: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    current_value: PropTypes.any,
    extra_data: PropTypes.shape({}),
  }).isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
};

const HistoryDrawer = ({
  open,
  handleClose,
  attribute,
  entityId,
  entityType,
}) => {
  const { getHistory } = useLog();

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(EMPTY_ARRAY);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.NEWEST);

  const attributeFilter = useMemo(() => {
    if (!attribute?.name) {
      return null;
    }
    return [
      {
        type: ATTRIBUTE_LOG_TYPE,
        attribute_name: attribute?.name,
        attribute_label: attribute?.label,
        attribute_id: attribute?.id,
      },
    ];
  }, [attribute?.name, attribute?.label, attribute?.id]);

  useEffect(() => {
    if (!open) {
      setHistory(EMPTY_ARRAY);
      setSearchTerm('');
      setSortOrder(SORT_ORDER.NEWEST);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !entityId || !entityType || !attributeFilter) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getHistory({
      entityType,
      entityId,
      attributes: attributeFilter,
      globalSearch: false,
      text: '',
      logActions: ['Attribute'],
      state: [],
      users: [],
      dateRange: null,
    })
      .then(
        (res) => !cancelled && setHistory(res?.data?.history || EMPTY_ARRAY)
      )
      .catch(() => !cancelled && setHistory(EMPTY_ARRAY))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, entityId, entityType, attributeFilter]);

  const filteredHistory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const base = term
      ? history.filter((entry) => {
          const userText = entry?.responsible_user?.toLowerCase() || '';
          const valueText = valueToSearchText(
            entry?.current_value,
            entry?.extra_data
          )
            .toString()
            .toLowerCase();
          return userText.includes(term) || valueText.includes(term);
        })
      : history;

    const sorted = [...base].sort((a, b) => {
      const aTime = a?.modification_date
        ? new Date(a.modification_date).getTime()
        : 0;
      const bTime = b?.modification_date
        ? new Date(b.modification_date).getTime()
        : 0;
      return sortOrder === SORT_ORDER.NEWEST ? bTime - aTime : aTime - bTime;
    });

    return sorted;
  }, [history, searchTerm, sortOrder]);

  // The "Current Value" header always reflects the latest entry
  const latestEntry = useMemo(() => {
    if (!history?.length) {
      return null;
    }
    return [...history].sort((a, b) => {
      const aTime = a?.modification_date
        ? new Date(a.modification_date).getTime()
        : 0;
      const bTime = b?.modification_date
        ? new Date(b.modification_date).getTime()
        : 0;
      return bTime - aTime;
    })[0];
  }, [history]);

  const currentValueText = useMemo(() => {
    if (!latestEntry) {
      return '-';
    }
    const text = valueToSearchText(
      latestEntry?.current_value,
      latestEntry?.extra_data
    );
    return text || '-';
  }, [latestEntry]);

  const handleSearchChangeImmediate = useCallback(
    (event) => {
      setSearchTerm(event?.target?.value || '');
    },
    [setSearchTerm]
  );

  const debouncedSearchChange = useMemo(
    () => debounce(handleSearchChangeImmediate, 250),
    [handleSearchChangeImmediate]
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleToggleSort = useCallback(() => {
    setSortOrder((prev) =>
      prev === SORT_ORDER.NEWEST ? SORT_ORDER.OLDEST : SORT_ORDER.NEWEST
    );
  }, [setSortOrder]);

  const changesCount = useMemo(() => {
    return history?.length || 0;
  }, [history]);

  const sortLabel = useMemo(() => {
    return sortOrder === SORT_ORDER.NEWEST ? 'Newest first' : 'Oldest first';
  }, [sortOrder]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{ keepMounted: false }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <Grid
        container
        direction="column"
        width={DRAWER_WIDTH}
        height="100%"
        flexWrap="nowrap"
      >
        <Grid
          item
          container
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
        >
          <Typography variant="h5">
            History{attribute?.label ? `: ${attribute.label}` : ''}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close history drawer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Divider />

        <Grid
          item
          container
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
          spacing={1}
          flexWrap="nowrap"
        >
          <Grid item xs sx={{ minWidth: 0 }}>
            <Grid container spacing={1} alignItems="center" flexWrap="nowrap">
              <Grid item>
                <Typography variant="body8" color="text.secondary">
                  Current Value
                </Typography>
              </Grid>
              <Grid item xs sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={currentValueText || ''}
                >
                  {currentValueText}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item sx={{ flexShrink: 0 }}>
            <Typography variant="body8" color="text.secondary">
              {changesCount} {changesCount === 1 ? 'change' : 'changes'}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          item
          container
          spacing={1}
          alignItems="center"
          px={2}
          pb={1.5}
          flexWrap="nowrap"
        >
          <Grid item xs>
            <SearchTextField
              width="100%"
              onChange={debouncedSearchChange}
              placeholder="Search by users, values"
              aria-label="Search history"
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapVertIcon fontSize="small" />}
              onClick={handleToggleSort}
            >
              {sortLabel}
            </Button>
          </Grid>
        </Grid>
        <Divider />

        {/* Timeline  */}
        <Box flex="1 1 auto" overflow="auto" px={2} py={2}>
          {loading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={6}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {!loading && filteredHistory?.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={6}
            >
              <NoDataPresentComponent message="No history available." />
            </Box>
          ) : null}

          {!loading && filteredHistory?.length > 0
            ? filteredHistory?.map((entry, index) => (
                <HistoryTimelineItem
                  key={`${entry?.modification_date || 'row'}-${entry?.responsible_user || 'unknown'}-${entry?.id ?? entry?.field ?? ''}`}
                  entry={entry}
                  isFirst={index === 0}
                  isLast={index === filteredHistory?.length - 1}
                />
              ))
            : null}
        </Box>
      </Grid>
    </Drawer>
  );
};

HistoryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
  }),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
};

export default HistoryDrawer;
